import type {
  Accounts,
  AccountRangePerGroup,
  AccountId,
  CapitalisedLocale,
  DigestRecommendationsPayload,
  GroupId,
  MappedAccountsOfGroup,
  Groups,
  GroupCollapse,
  GroupHierarchy,
  GroupMappedAccounts,
  GroupMappedAccountAssociation,
  MappedAccountIds,
  MappingProposal,
  RawAccount,
  RawGroup,
  AccountBalance,
  ReservedPostfixes,
  SaveMappingError,
  SaveMappingResponse,
  AsyncSaveMappingResponse,
} from '../shared-types'
import { isDigestRecommendationsSuccessPayload } from '../shared-types'
import type { CommonState } from './types'

import chunk from 'lodash/chunk'
import pick from 'lodash/pick'
import { defineStore } from 'pinia'

import {
  fetchAccounts,
  fetchAccountGroups,
  fetchMappingProposals,
  fetchAccountBalances,
} from '@app/application/account-mapping/api'
import formatResponseData from '@app/application/account-mapping/store/utils/formatResponseData'
import { makeQueryHierarchy } from './utils/hierarchy'
import {
  calculateReservedPostfixes,
  mappedNumberResolver,
} from './utils/mapped-number-resolution'
import { getAccountRangeAndPostfix } from './utils/account-numbers'
import formatJsonRequestData from '@app/application/account-mapping/store/utils/formatJsonRequestData'
import type { AccountMappingState } from './utils/mapping-priority-resolver'
import {
  canAIOverride,
  needsRemovalFromCurrentGroup,
} from './utils/mapping-priority-resolver'
import SFetch from '@app/utils/sfetch'
import { useLocale } from './locale'

export const useCommon = defineStore('accountMappingCommon', {
  state: (): CommonState => ({
    accounts: {},
    accountBalances: {},
    accountIds: [],
    groups: {},
    groupCollapse: {},
    groupHierarchy: {},
    groupList: [],
    groupMappedAccounts: {},
    isFetching: false,
    recommendations: {},
    remapSearchFocusedAt: 0,
    searchTerm: '',
    shouldUsePostfixes: false,
    shouldUseOriginalNames: false,
    correlationId: null,
  }),
  actions: {
    addAccountsToGroup({
      groupId,
      accountIds,
    }: {
      groupId: GroupId
      accountIds: AccountId[]
    }) {
      this.commitAddAccountToGroup({
        uuid: groupId,
        accountIds,
      })

      if (this.groupCollapse?.[groupId]) {
        delete this.groupCollapse[groupId]
      }
    },
    addAccountToGroupFromRecommendations({
      accountRange,
      accountId,
    }: {
      accountRange: string
      accountId: AccountId
    }) {
      const rangePerGroup = this.getAccountRangePerGroup
      const groupId = rangePerGroup[accountRange]

      if (!groupId) return

      const mappingState: AccountMappingState = {
        isMapped: !!this.getMappedAccountIds[accountId],
        isSaved: this.getFlatMappedAccounts[accountId]?.isSaved || false,
        remappedByUser: !!this.accounts[accountId]?.remappedByUser,
        proposalSource:
          this.accounts[accountId]?.internalOrExternalProposal?.source,
      }

      if (!canAIOverride(mappingState)) return

      if (needsRemovalFromCurrentGroup(mappingState)) {
        const currentGroupId = this.getMappedAccountIds[accountId]
        this.commitRemoveAccountFromGroup({
          accountId,
          uuid: currentGroupId,
        })
      }

      this.addAccountsToGroup({
        groupId,
        accountIds: [accountId],
      })
    },
    clearSearch() {
      this.searchTerm = ''
    },
    collapseGroup(uuid: GroupId) {
      this.groupCollapse[uuid] = true
    },
    commitAddAccountToGroup({
      uuid,
      accountIds,
    }: {
      uuid: GroupId
      accountIds: Array<AccountId>
    }) {
      const previouslyMappedAccounts = this.groupMappedAccounts?.[uuid]
        ? { ...this.groupMappedAccounts[uuid] }
        : {}

      // If we can't use postfixes, then we can only add one account to a group
      const hasMaximumMappedAccounts = this.shouldUsePostfixes
        ? false
        : Object.keys(previouslyMappedAccounts).length === 1

      const accountRange = this.groups[uuid].accountRange
      const numberResolver = mappedNumberResolver({
        accountRange,
        mappedAccounts: previouslyMappedAccounts,
        reservedPostfixes: this.getReservedPostfixes[accountRange],
        shouldUsePostfixes: this.shouldUsePostfixes,
      })

      const effectiveMappedAccounts = {
        // Delete the previously mapped accounts if we can't use postfixes
        ...(hasMaximumMappedAccounts ? {} : previouslyMappedAccounts),
        ...accountIds.reduce((acc, accountId) => {
          const initialNumber =
            this.accounts[accountId]?.initialMappedNumber || ''

          const [initialAccountRange, initialPostfix] =
            getAccountRangeAndPostfix(initialNumber)

          const isAlreadyPostfixed =
            initialPostfix && initialAccountRange === accountRange

          const mappedNumber = isAlreadyPostfixed
            ? initialNumber
            : numberResolver.generate()

          // Check the initial mapped number for this account
          // If it exists and we mapped back to it, it means it reverted back to its original, pristine, saved state
          const mappedBackToSavedNumber =
            this.accounts[accountId]?.initialMappedNumber === mappedNumber &&
            this.accounts[accountId]?.mapped

          acc[accountId] = {
            isSaved: mappedBackToSavedNumber,
            mappedNumber,
          }

          return acc
        }, {} as MappedAccountsOfGroup),
      }

      this.groupMappedAccounts = {
        ...this.groupMappedAccounts,
        [uuid]: effectiveMappedAccounts,
      }
    },
    commitRemoveAccountFromGroup({
      accountId,
      uuid,
    }: {
      accountId: AccountId
      uuid: GroupId
    }): void {
      const mappedAccounts = this.groupMappedAccounts?.[uuid] || {}

      if (mappedAccounts[accountId]) {
        if (Object.keys(mappedAccounts).length === 1) {
          delete this.groupMappedAccounts[uuid]
        } else {
          delete this.groupMappedAccounts[uuid][accountId]
        }
      }
    },
    commitMarkAccountAsRemappedByUser(accountId: AccountId) {
      const account = this.accounts[accountId]

      this.accounts[accountId] = {
        ...account,
        remappedByUser: true,
      }
    },
    async fetchInitialData({
      shouldUsePostfixes,
    }: {
      shouldUsePostfixes: boolean
    }) {
      this.isFetching = true

      try {
        // Always fetch internal proposals - they will be merged with AI proposals
        const useInternalMappingProposals = true

        const [accountData, groupData, mappingProposalData] =
          (await Promise.all([
            fetchAccounts(),
            fetchAccountGroups(),
            useInternalMappingProposals
              ? fetchMappingProposals()
              : async (): Promise<void> => {},
          ])) as [
            Record<AccountId, RawAccount>,
            RawGroup[],
            Record<AccountId, MappingProposal>
          ]

        const { supportedLocales } = useLocale()

        const {
          accounts,
          accountIds,
          groups,
          groupCollapse,
          groupHierarchy,
          groupList,
          groupMappedAccounts,
        } = formatResponseData({
          accounts: accountData,
          accountGroups: groupData,
          mappingProposals: mappingProposalData,
          useInternalMappingProposals,
          shouldUsePostfixes,
          supportedLocales,
        })

        this.receiveAccounts({
          accounts,
          accountIds,
        })

        this.receiveAccountGroups({
          groups,
          groupCollapse,
          groupHierarchy,
          groupList,
          groupMappedAccounts,
        })
      } catch (e) {
        if (window.Sentry) {
          window.Sentry.captureMessage('Failed to load new account mapping', {
            level: 'info',
            extra: {
              ...window.Silverfin.jsonParams,
              error: e,
            },
          })
        }
      }

      this.isFetching = false
    },
    async fetchAccountBalancesData() {
      const accountBalances = (await fetchAccountBalances()) as Record<
        AccountId,
        AccountBalance
      >
      this.receiveAccountBalances(accountBalances)
    },
    expandGroup(groupId: GroupId) {
      delete this.groupCollapse[groupId]

      //expand the child-group if there is exactly 1
      const groupHierarchyEntity = this.groupHierarchy[groupId]
      if (groupHierarchyEntity.children.length === 1) {
        const childGroupId = groupHierarchyEntity.children[0]
        this.expandGroup(childGroupId)
      }
    },
    focusRemapSearch() {
      this.remapSearchFocusedAt = Date.now()
    },
    receiveAccounts({
      accounts,
      accountIds,
    }: {
      accounts: Accounts
      accountIds: AccountId[]
    }) {
      this.accountIds = accountIds

      accountIds.forEach((accountId) => {
        const account = accounts[accountId]
        this.accounts[account.id] = account
      })
    },
    receiveAccountGroups({
      groups,
      groupCollapse,
      groupHierarchy,
      groupList,
      groupMappedAccounts,
    }: {
      groups: Groups
      groupCollapse: GroupCollapse
      groupHierarchy: GroupHierarchy
      groupList: Array<GroupId>
      groupMappedAccounts: GroupMappedAccounts
    }) {
      Object.keys(groups).forEach((key) => {
        this.groups[key] = groups[key]
      })

      Object.keys(groupCollapse).forEach((key) => {
        this.groupCollapse[key] = groupCollapse[key]
      })

      Object.keys(groupHierarchy).forEach((key) => {
        this.groupHierarchy[key] = groupHierarchy[key]
      })

      this.groupList = groupList

      Object.keys(groupMappedAccounts).forEach((key) => {
        this.groupMappedAccounts[key] = groupMappedAccounts[key]
      })
    },
    receiveAccountBalances(data: Record<AccountId, AccountBalance>) {
      Object.keys(data).forEach((key) => {
        this.accountBalances[key] = data[key]
      })
    },
    receiveRecommendations(data: DigestRecommendationsPayload) {
      if (isDigestRecommendationsSuccessPayload(data)) {
        data.accounts.forEach((recommendation) => {
          this.recommendations[recommendation.id] = recommendation
        })
        this.correlationId = data.correlation_id || null
      }
    },

    async saveMappingAsync(payload: {
      url: string
      authenticityToken: string
      supportedLocales: CapitalisedLocale[]
    }): Promise<AsyncSaveMappingResponse> {
      const commonStore = useCommon()
      const mappedAccountIds = Object.keys(commonStore.getMappedAccountIds)
      const requestData = {
        accounts: commonStore.$state.accounts,
        groups: commonStore.$state.groups,
        groupMappedAccounts: commonStore.$state.groupMappedAccounts,
        shouldUseOriginalNames: commonStore.$state.shouldUseOriginalNames,
      }

      const data = formatJsonRequestData({
        ...requestData,
        mappedAccountIds: pick(
          commonStore.getUnsavedMappedAccountIds,
          mappedAccountIds
        ),
        supportedLocales: payload.supportedLocales,
        recommendations: commonStore.$state.recommendations,
      })

      const body = {
        accounts: data,
        correlation_id: commonStore.correlationId,
      }
      const response = await SFetch.json<AsyncSaveMappingResponse>(
        payload.url,
        {
          body,
          method: 'POST',
        }
      )

      if (!response) {
        throw new Error('Received null response from SFetch.json')
      }

      const messageBusChannelName = response.messageBusChannelName
      return {
        messageBusChannelName,
      }
    },

    async saveMappingBatched(payload: {
      url: string
      method: 'GET' | 'POST'
      authenticityToken: string
      saveBatchSize: number
      supportedLocales: CapitalisedLocale[]
    }): Promise<SaveMappingResponse> {
      const commonStore = useCommon()
      const mappedAccountIds = Object.keys(commonStore.getMappedAccountIds)
      const mappedAccountIdsChunks = chunk(
        mappedAccountIds,
        payload.saveBatchSize
      )

      const requestData = {
        accounts: commonStore.$state.accounts,
        groups: commonStore.$state.groups,
        groupMappedAccounts: commonStore.$state.groupMappedAccounts,
        shouldUseOriginalNames: commonStore.$state.shouldUseOriginalNames,
      }

      // Gather all responses and errors
      const responsesAndErrors = []
      const rawErrors = []

      for (const chunk of mappedAccountIdsChunks) {
        const data = formatJsonRequestData({
          ...requestData,
          mappedAccountIds: pick(commonStore.getUnsavedMappedAccountIds, chunk),
          supportedLocales: payload.supportedLocales,
          recommendations: commonStore.$state.recommendations,
        })

        const body = {
          accounts: data,
          correlation_id: commonStore.correlationId,
        }

        try {
          const response = await SFetch.json<SaveMappingResponse>(payload.url, {
            body,
            method: 'POST',
          })
          responsesAndErrors.push(response)
        } catch (e) {
          const errorBody = (e as SaveMappingError).body
          if (errorBody?.errorType === 'concurrency') {
            throw e
          }
          rawErrors.push(errorBody)
          responsesAndErrors.push(errorBody)
        }
      }

      const errors = rawErrors.flatMap((rawError) => rawError.errors)
      const nextPath = responsesAndErrors
        .reverse()
        .find((responseOrError) => !!responseOrError?.nextPath)?.nextPath

      return {
        errors,
        nextPath,
      } as SaveMappingResponse
    },
    searchFor(searchTerm: string) {
      this.searchTerm = searchTerm
    },
    usePostfixes(shouldUsePostfixes: boolean) {
      this.shouldUsePostfixes = shouldUsePostfixes
    },
    useOriginalNames(shouldUseOriginalNames: boolean) {
      this.shouldUseOriginalNames = shouldUseOriginalNames
    },
  },
  getters: {
    getExternalNumbersByAccountRange: (state) => {
      return Object.keys(state.accounts).reduce((acc, accountId) => {
        const account = state.accounts[accountId]
        if (account.externallyMappedNumber) {
          if (acc[account.externallyMappedNumber]) {
            acc[account.externallyMappedNumber].push(accountId)
          } else {
            acc[account.externallyMappedNumber] = [accountId]
          }
        }
        return acc
      }, {} as Record<string, AccountId[]>)
    },
    getAccountRangePerGroup: (state) => {
      return Object.keys(state.groups).reduce((acc, groupId) => {
        const group = state.groups[groupId]

        if (group.accountRange) {
          acc[group.accountRange] = groupId
        }

        return acc
      }, {} as AccountRangePerGroup)
    },
    getFlatMappedAccounts(
      state
    ): Record<AccountId, GroupMappedAccountAssociation> {
      const mappingsPerGroup = state.groupMappedAccounts
      const mappingsPerOriginalAccount = {} as Record<
        AccountId,
        GroupMappedAccountAssociation
      >

      for (const groupId in mappingsPerGroup) {
        const mappings = mappingsPerGroup[groupId]
        for (const accountId in mappings) {
          mappingsPerOriginalAccount[accountId] = {
            groupId,
            ...mappings[accountId],
          }
        }
      }
      return mappingsPerOriginalAccount
    },
    getGroupListPerDisplay(state): Array<GroupId> {
      return state.groupList
    },
    getHasUnsavedChanges(): boolean {
      return Object.keys(this.getUnsavedMappedAccountIds).length > 0
    },
    getMappedAccountIds: (state) => {
      return Object.keys(state.groupMappedAccounts).reduce((acc, key) => {
        const accountIds = Object.keys(state?.groupMappedAccounts[key] || {})

        accountIds.forEach((accountId) => {
          acc[accountId] = key
        })

        return acc
      }, {} as MappedAccountIds)
    },
    getReservedPostfixes(state): ReservedPostfixes {
      return calculateReservedPostfixes(state.accounts)
    },
    getUnsavedGroupsAndAncestors: (state) => {
      const groupHierarchy = state.groupHierarchy
      const groupMappedAccounts = state.groupMappedAccounts

      const doesGroupContainUnsaved = (groupId: GroupId): boolean => {
        if (!groupMappedAccounts[groupId]) {
          return false
        }

        return Object.keys(groupMappedAccounts[groupId]).some(
          (key) => !groupMappedAccounts[groupId][key].isSaved
        )
      }

      const queryForUnsaved = makeQueryHierarchy({
        predicate: doesGroupContainUnsaved,
      })

      return queryForUnsaved(groupHierarchy)
    },
    getUnsavedMappedAccountIds: (state) => {
      const unsavedMappedAccounts = {} as MappedAccountIds

      for (const groupId in state.groupMappedAccounts) {
        const group = state.groupMappedAccounts[groupId]
        for (const accountId in group) {
          const mappedAccount = group[accountId]
          if (!mappedAccount.isSaved) {
            unsavedMappedAccounts[accountId] = groupId
          }
        }
      }

      return unsavedMappedAccounts
    },
  },
})
